import * as Phaser from "phaser-ce"
import { log } from "../../../../common/Logging"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { BackgroundSprite } from "../../../generic-sprites/BackgroundSprite"
import { CigaretteSprite } from "../../../generic-sprites/CigaretteSprite"
import { loadGenericAssets } from "../../../generic-sprites/GenericAssets"
import { Sound } from "../../../generic-sprites/lip-sync/Sound"
import { Word } from "../../../generic-sprites/lip-sync/Word"
import { ScientistSprite } from "../../../generic-sprites/ScientistSprite"
import { ThrobberSprite } from "../../../generic-sprites/ThrobberSprite"
import { TitleSprite } from "../../../generic-sprites/TitleSprite"
import { AudioSync } from "../../../utils/AudioSync"
import { CameraZoom, ZoomInfo, ZoomTarget } from "../../../utils/CameraZoom"
import { ColorUtils } from "../../../utils/ColorUtils"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { AddictionRepo } from "../AddictionRepo"
import { loadAssets, PhysicalAssets } from "./PhysicalAssets"
import { PhysPos } from "./PhysPos"

export class PhysicalState extends Phaser.State {

    private static readonly SIT = "SIT"
    private static readonly HOP = "HOP"
    private static readonly EXPLODE = "EXPLODE"
    private static readonly DIE = "DIE"

    private scientist: ScientistSprite
    private physicalBrain: Phaser.Sprite
    private darkBrain: ThrobberSprite
    private cigarette: CigaretteSprite
    private firstScene: Phaser.Group
    private secondScene: Phaser.Group
    private titleGroup: Phaser.Group
    private scientistGroup: Phaser.Group
    private frog: Phaser.Sprite
    private flameLeft: Phaser.Sprite
    private bubblesLeft: Phaser.Sprite

    private oneOffs: AudioSync

    private readonly words: Array<Word> = [
        new Word(314, 527, [Sound.FV, Sound.L], "Physical"),
        new Word(581, 1173, [Sound.A, Sound.CON], "addiction"),
        new Word(1174, 1671, [Sound.A, Sound.MBP, Sound.CON], "happens"),
        new Word(1672, 1943, [Sound.E, Sound.CON], "when the"),
        new Word(2032, 2429, [Sound.E, Sound.MBP, Sound.L], "chemical"),
        new Word(2476, 2607, [Sound.CON], "in"),
        new Word(2654, 3116, [Sound.CON, Sound.O, Sound.MBP, Sound.O], "tobacco"),
        new Word(3175, 3329, [Sound.L], "called"),
        new Word(3330, 3945, [Sound.CON, Sound.O, Sound.CON], "nicotine"),
        new Word(4253, 4603, [Sound.E, Sound.CON], "enters"),
        new Word(4662, 4763, [Sound.CON], "the"),
        new Word(4828, 5065, [Sound.MBP, Sound.O], "body."),

        new Word(20923, 21522, [Sound.CON, Sound.O, Sound.CON], "the nicotine"),
        new Word(21575, 21960, [Sound.A, Sound.CON], "changes"),
        new Word(21961, 22149, [Sound.U], "your"),
        new Word(22232, 22511, [Sound.MBP, Sound.A], "brain,"),
        new Word(22955, 23400, [Sound.A, Sound.O, Sound.U], "and how you"),
        new Word(23488, 23761, [Sound.FV, Sound.E, Sound.L], "feel."),
        new Word(25000, 25976, [Sound.O, Sound.U, Sound.E, Sound.L, Sound.CON], "Before you realize"),
        new Word(25977, 26622, [Sound.A, Sound.MBP, Sound.CON], "what's happening,"),
        new Word(26711, 27262, [Sound.U, Sound.A, Sound.CON], "you're addicted."),

        new Word(28447, 28920, [Sound.CON, Sound.O], "Addiction"),
        new Word(28921, 29200, [Sound.E], "sneaks"),
        new Word(29264, 29418, [Sound.U, Sound.MBP], "up"),
        new Word(29548, 29791, [Sound.O, Sound.U], "on you"),
        new Word(29933, 30176, [Sound.E, Sound.CON], "because"),
        new Word(30177, 30763, [Sound.CON, Sound.O, Sound.CON], "nicotine is"),
        new Word(30846, 31414, [Sound.A, Sound.CON], "changing your"),
        new Word(31456, 31693, [Sound.MBP, Sound.A], "brain"),
        new Word(31734, 32279, [Sound.CON, Sound.U, Sound.L], "gradually"),
        new Word(32362, 32600, [Sound.O, Sound.FV], "over"),
        new Word(32688, 33014, [Sound.CON, Sound.MBP], "time."),

        new Word(34200, 34720, [Sound.E, Sound.O, Sound.U], "Before you"),
        new Word(34721, 35289, [Sound.E, Sound.L, Sound.CON], "realize it,"),
        new Word(35597, 36171, [Sound.U, Sound.O], "you're hooked."),
        new Word(37309, 37475, [Sound.CON], "It's"),
        new Word(37575, 37913, [Sound.CON, Sound.L], "just like"),
        new Word(38067, 38262, [Sound.CON], "this"),
        new Word(38387, 38648, [Sound.FV, Sound.O], "frog."),

        new Word(39572, 39631, [Sound.CON], "It"),
        new Word(39702, 39767, [Sound.U], "would"),
        new Word(39850, 40460, [Sound.E, Sound.FV, Sound.A, Sound.A], "never stay in a"),
        new Word(40585, 40875, [Sound.MBP, Sound.CON], "beaker"),
        new Word(40982, 41722, [Sound.FV, Sound.O, Sound.L, Sound.A], "of boiling water."),

        new Word(57444, 58048, [Sound.O, Sound.CON, Sound.CON], "along with it."),
        new Word(59138, 59648, [Sound.CON, Sound.A], "This is what"),
        new Word(59649, 60104, [Sound.L, Sound.A, Sound.MBP], "will happen"),
        new Word(60139, 60548, [Sound.O, Sound.U], "to you"),
        new Word(60643, 61158, [Sound.FV, Sound.U, Sound.U], "if you use"),
        new Word(61223, 61638, [Sound.CON, Sound.O, Sound.MBP, Sound.O], "tobacco."),

        new Word(62373, 62693, [Sound.U, Sound.O], "You won't"),
        new Word(62781, 63321, [Sound.L, Sound.U], "realize you're"),
        new Word(63368, 63943, [Sound.E, Sound.CON, Sound.O], "getting more"),
        new Word(64008, 64304, [Sound.A, Sound.O], "and more"),
        new Word(64440, 64940, [Sound.FV, Sound.A, Sound.L, Sound.A], "physically a-"),
        new Word(65038, 65311, [Sound.CON], "ddicted"),
        new Word(65429, 65613, [Sound.E], "each"),
        new Word(65660, 66324, [Sound.CON, Sound.U, Sound.O], "time you smoke,"),
        new Word(66910, 67088, [Sound.FV, Sound.MBP], "vape"),
        new Word(67384, 67769, [Sound.O, Sound.CON], "or chew."),

        new Word(68717, 68871, [Sound.O], "Once"),
        new Word(68907, 69576, [Sound.U, Sound.A, Sound.CON], "you're addicted,"),
        new Word(69967, 70115, [Sound.L], "you'll"),
        new Word(70204, 70654, [Sound.FV, Sound.CON, Sound.A], "find it hard"),
        new Word(70655, 71205, [Sound.O, Sound.O, Sound.CON, Sound.O], "to go without"),
        new Word(71270, 71685, [Sound.CON, Sound.O, Sound.MBP, Sound.O], "tobacco,"),
        new Word(72141, 72781, [Sound.E, Sound.E, Sound.U, Sound.A], "even when you want"),
        new Word(72888, 72994, [Sound.O], "to."),
    ]

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)

        this.game.add.existing(new BackgroundSprite(this.game, PhysicalAssets.BACKGROUND))

        this.firstScene = this.game.add.group()
        this.scientistGroup = this.game.add.group()
        this.secondScene = this.game.add.group()
        this.titleGroup = this.game.add.group()
        this.secondScene.visible = false

        new TitleSprite(this.game, PhysPos.titleY(), "Physical Addiction", true, this.titleGroup)

        this.physicalBrain = this.firstScene.create(PhysPos.physicalBrainX(), PhysPos.physicalBrainY(),
            PhysicalAssets.PHYSICAL_BRAIN)
        Scale.scaleSprite(this.physicalBrain)
        this.darkBrain = new ThrobberSprite(this.game, PhysPos.physicalBrainX(), PhysPos.physicalBrainY(),
            PhysicalAssets.JUST_BRAIN)
        this.firstScene.add(this.darkBrain)
        this.cigarette = new CigaretteSprite(this.game, PhysPos.cigaretteX(), PhysPos.cigaretteY(), 0.38, this.firstScene)
        this.cigarette.visible = false

        const desk = this.firstScene.create(PhysPos.deskX(), PhysPos.deskY(), PhysicalAssets.FULL_DESK)
        Scale.scaleSprite(desk, 0.7)

        const paTitle = this.firstScene.create(0, 0, PhysicalAssets.PA_TITLE)
        Scale.scaleSprite(paTitle, 0.7)
        Sprites.centerSpriteInSprite(this.physicalBrain, paTitle)
        paTitle.y = PhysPos.paTitleY()

        this.scientist = new ScientistSprite(this.game, PhysPos.scientistX(), PhysPos.scientistY(), PhysicalAssets.AUDIO,
            0.75, this.words, this.scientistGroup)

        this.scientist.audio.onStop.add(() => {
            AddictionRepo.instance.setPhysicalAsComplete()
            const experimentOver = this.game.time.create(true)
            experimentOver.add(1500, () => {
                this.game.state.start(ActivityKey.ADDICTION_MAIN)
            })
            experimentOver.start()
        })

        this.setupFirstSceneTimers()
    }

    setupFirstSceneTimers = () => {
        let undoZoom: ZoomInfo
        const oneOffs = new AudioSync(this.game, this.scientist.audio)
        this.oneOffs = oneOffs

        oneOffs.add(5300, () => {
            undoZoom = CameraZoom.zoom(this.game, this.physicalBrain)
            this.titleGroup.destroy()
        })
        oneOffs.add(8500, () => {
            this.cigarette.visible = true
            this.cigarette.smoke()
        })

        this.darkBrain.throb(0, 30000)

        oneOffs.add(15000, () => {
            const darkenBrainTimer = this.game.time.create(false)
            ColorUtils.shadeInALoop(this.darkBrain, darkenBrainTimer, 60, "#55552b", 0.2)
            darkenBrainTimer.start()
        })

        oneOffs.add(22000, () => {
            CameraZoom.performZoom(undoZoom)
        })

        oneOffs.add(28000, this.createSecondScene)
        this.setupSecondSceneTimers()
        oneOffs.start()
    }

    createSecondScene = () => {
        this.firstScene.destroy()
        this.secondScene.visible = true
        this.scientist.x = PhysPos.scientistX2()
        this.scientist.y = PhysPos.scientistY2()
        Scale.scaleSprite(this.scientist, 1)
        this.scientist.changeImage(PhysicalAssets.SCIENTIST_FROG, 1, this.scientist.x + Scale.width() * 0.25,
            this.scientist.y + Scale.height() * 0.35)

        const desk2 = this.secondScene.create(PhysPos.deskX2(), PhysPos.deskY2(), PhysicalAssets.DESK_2)
        Scale.scaleSprite(desk2, 0.8)

        const bunsenScale = 0.72
        const beakerScale = 0.78

        this.flameLeft = this.secondScene.create(PhysPos.flameLeftX(), PhysPos.flameLeftY(),
            PhysicalAssets.FLAME)
        Scale.scaleSprite(this.flameLeft, bunsenScale)
        this.flameLeft.visible = false
        const bunsenLeft = this.secondScene.create(PhysPos.bunsenLeftX(), PhysPos.bunsenLeftY(),
            PhysicalAssets.BUNSEN_LEFT)
        Scale.scaleSprite(bunsenLeft, bunsenScale)

        const flameRight: Phaser.Sprite = this.secondScene.create(PhysPos.flameRightX(), PhysPos.flameRightY(),
            PhysicalAssets.FLAME)
        Scale.scaleSprite(flameRight, bunsenScale)
        flameRight.animations.add("burn", [0, 1, 2, 3, 4, 3, 2, 1]).play(6, true)
        const bunsenRight = this.secondScene.create(PhysPos.bunsenRightX(), PhysPos.bunsenRightY(),
            PhysicalAssets.BUNSEN_RIGHT)
        Scale.scaleSprite(bunsenRight, bunsenScale)

        this.frog = this.secondScene.create(PhysPos.frogX(), PhysPos.frogY(), PhysicalAssets.FROG)
        Scale.scaleSprite(this.frog, 0.45)
        this.frog.visible = false

        const beakerLeft = this.secondScene.create(PhysPos.beakerLeftX(), PhysPos.beakerY(), PhysicalAssets.BEAKER)
        Scale.scaleSprite(beakerLeft, beakerScale)
        this.bubblesLeft = this.secondScene.create(PhysPos.bubblesLeftX(), PhysPos.bubblesY(),
            PhysicalAssets.BUBBLES)
        Scale.scaleSprite(this.bubblesLeft, bunsenScale)
        this.bubblesLeft.visible = false

        const beakerRight = this.secondScene.create(PhysPos.beakerRightX(), PhysPos.beakerY(), PhysicalAssets.BEAKER)
        Scale.scaleSprite(beakerRight, beakerScale)
        const bubblesRight: Phaser.Sprite = this.secondScene.create(PhysPos.bubblesRightX(), PhysPos.bubblesY(),
            PhysicalAssets.BUBBLES)
        Scale.scaleSprite(bubblesRight, bunsenScale)
        bubblesRight.animations.add("bubble", [4, 5, 6, 7, 8, 9, 10, 11]).play(4, true)

    }

    setupSecondSceneTimers = () => {
        let undoZoom: ZoomInfo
        const oneOffs = this.oneOffs
        oneOffs.setOffset(28000)
        oneOffs.add(10000, () => {
            this.scientist.frame = 1

            this.frog.visible = true
            this.frog.animations.add(PhysicalState.SIT, [0])
            this.frog.animations.add(PhysicalState.HOP, [1, 2])
            this.frog.animations.add(PhysicalState.EXPLODE, [3])
            this.frog.animations.add(PhysicalState.DIE, [4])

        })

        oneOffs.add(12750, () => {
            const target = new ZoomTarget()
            target.x = 0
            target.y = Scale.height() * 0.2
            target.width = Scale.width() * 0.7
            target.height = Scale.height() * 0.7
            undoZoom = CameraZoom.zoom(this.game, target, 500)
        })

        // Hop into first boiling beaker.
        oneOffs.add(13500, () => {
            this.frog.animations.play(PhysicalState.HOP)

            CameraZoom.moveSpriteAlongParabola(this.frog, PhysPos.beaker2PlatformX(), PhysPos.frogVertexX1(),
                PhysPos.frogVertexY1(), 900)
        })

        oneOffs.add(14400, () => {
            this.frog.animations.play(PhysicalState.SIT)
        })

        oneOffs.add(14600, () => {
            this.frog.animations.play(PhysicalState.EXPLODE)
        })

        // Hop out of first boiling beaker.
        oneOffs.add(14800, () => {
            this.frog.animations.play(PhysicalState.HOP)
            CameraZoom.moveSpriteAlongParabola(this.frog, PhysPos.frogDeskX(), PhysPos.frogVertexX2(), PhysPos.frogVertexY2(),
                1000)
        })

        oneOffs.add(15800, () => {
            this.frog.animations.play(PhysicalState.SIT)
            this.scientist.frame = 0
        })

        // Hop into second boiling beaker.
        oneOffs.add(18000, () => {
            CameraZoom.moveSpriteAlongParabola(this.frog, PhysPos.beaker1PlatformX(), PhysPos.frogVertexX3(),
                PhysPos.frogVertexY3(), 1000)
            this.frog.animations.play(PhysicalState.HOP)
        })

        oneOffs.add(19000, () => {
            this.frog.animations.play(PhysicalState.SIT)
        })

        oneOffs.add(19000, () => {
            this.bubblesLeft.visible = true
            const bubble = this.bubblesLeft.animations.add("bubble")
            bubble.onComplete.addOnce(() => {
                this.bubblesLeft.animations.add("bubbleFast", [4, 5, 6, 7, 8, 9, 10, 11])
                    .play(6, true)
            })
            bubble.play(3)

            this.flameLeft.visible = true
            this.flameLeft.animations.add("burn", [0, 1, 2, 3, 4, 3, 2, 1]).play(10, true)
        })

        oneOffs.add(23000, () => {
            log.debug("frog die")
            this.frog.animations.play(PhysicalState.DIE)
            Scale.scaleSprite(this.frog, 0.39)
        })

        oneOffs.add(30000, () => {
            CameraZoom.performZoom(undoZoom)
        })
    }

}
